import type { Namespace } from '../types';

// DE drafted — queued for native review. Problem / Known-Error management +
// Knowledge-base lifecycle (R-143) — cloud-owned commons that replicate down to
// the sites (D6). Problems drive the known-error catalogue; KB carries the
// structured articles through draft → review → published → retired.
export const problems = {
  // R-349: each tab became its own page title/desc when the in-page tab strip
  // was dropped — "Problems & Knowledge" no longer distinguished which screen
  // was on-screen once there was nothing under it naming the other one.
  pageDescProblems: {
    de: 'Bekannte Fehler — cloud-eigen, an die Standorte gespiegelt.',
    en: 'Known-errors and the problem catalogue — cloud-owned, mirrored to the sites.',
  },
  pageDescKb: {
    de: 'Knowledge-Base-Artikel — cloud-eigen, an die Standorte gespiegelt.',
    en: 'The knowledge base — cloud-owned, mirrored to the sites.',
  },
  tabProblems: { de: 'Probleme', en: 'Problems' },
  tabKb: { de: 'Knowledge Base', en: 'Knowledge Base' },

  // ── shared ──
  colNumber: { de: 'Nummer', en: 'Number' },
  colTitle: { de: 'Titel', en: 'Title' },
  colStatus: { de: 'Status', en: 'Status' },
  colClass: { de: 'Klasse', en: 'Class' },
  filterAll: { de: 'Alle', en: 'All' },
  cancel: { de: 'Abbrechen', en: 'Cancel' },
  save: { de: 'Speichern', en: 'Save' },
  loadError: { de: 'Laden fehlgeschlagen', en: 'Failed to load' },
  retry: { de: 'Erneut versuchen', en: 'Retry' },
  none: { de: '—', en: '—' },

  // ── problems ──
  newProblem: { de: 'Neues Problem', en: 'New problem' },
  createProblem: { de: 'Problem anlegen', en: 'Create problem' },
  emptyProblems: { de: 'Keine Probleme', en: 'No problems' },
  emptyProblemsMsg: { de: 'Noch keine Probleme erfasst.', en: 'No problems recorded yet.' },
  emptyProblemsFiltered: {
    de: 'Keine Probleme mit diesem Status.',
    en: 'No problems with this status.',
  },
  emptyKbFiltered: { de: 'Keine Artikel in diesem Zustand.', en: 'No articles in this state.' },
  keFlag: { de: 'BEKANNTER FEHLER', en: 'KNOWN ERROR' },
  fieldTitle: { de: 'Titel', en: 'Title' },
  fieldTitlePh: {
    de: 'z. B. Pumpe P-102 löst wiederholt Sicherung aus',
    en: 'e.g. Pump P-102 keeps tripping the breaker',
  },
  fieldKbTitlePh: {
    de: 'z. B. Ausgelöste Pumpensicherung zurücksetzen',
    en: 'e.g. Resetting a tripped pump breaker',
  },
  fieldDescription: { de: 'Beschreibung', en: 'Description' },
  fieldDescriptionPh: {
    de: 'Was passiert, und wie es aufgefallen ist',
    en: "What's happening, and how it was noticed",
  },
  fieldClass: { de: 'Asset-Klasse', en: 'Asset class' },
  // R-350 (user-directed 2026-09-23): a free-text class code let an operator
  // type anything, including a value with no matching commons row — the FK
  // violation R-350's own edit-modal fix exists to sidestep. A picker sourced
  // from the same commons table the FK actually checks against can't do that.
  selectClassPh: { de: '— Klasse wählen —', en: '— select class —' },
  fieldRootCause: { de: 'Ursache (Root Cause)', en: 'Root cause' },
  fieldRootCausePh: {
    de: 'z. B. Lagerverschleiß durch fehlende Schmierung',
    en: 'e.g. Bearing wear from a missed lubrication interval',
  },
  fieldWorkaround: { de: 'Workaround', en: 'Workaround' },
  fieldWorkaroundPh: {
    de: 'z. B. Pumpe alle 4 Stunden manuell neu starten',
    en: 'e.g. Restart the pump manually every 4 hours',
  },
  fieldFixChangeNumber: { de: 'Fix-Change-Nr.', en: 'Fix change no.' },
  fieldFixChangeNumberPh: { de: 'z. B. CHG-1004', en: 'e.g. CHG-1004' },
  detailTitle: { de: 'Problemdetails', en: 'Problem details' },
  editDetails: { de: 'Details bearbeiten', en: 'Edit details' },
  sectionDetails: { de: 'Details', en: 'Details' },
  transitionTitle: { de: 'Status ändern', en: 'Change status' },
  transitionTo: { de: 'Weiter zu {status}', en: 'Move to {status}' },
  workaroundRequired: {
    de: 'Ein Workaround ist nötig, um „Bekannter Fehler" zu setzen.',
    en: 'A workaround is required to enter "known-error".',
  },
  workaroundPrompt: { de: 'Workaround (für bekannten Fehler)', en: 'Workaround (for known-error)' },
  status_new: { de: 'Neu', en: 'New' },
  status_assess: { de: 'Analyse', en: 'Assess' },
  status_known_error: { de: 'Bekannter Fehler', en: 'Known-error' },
  status_resolved: { de: 'Gelöst', en: 'Resolved' },
  status_closed: { de: 'Geschlossen', en: 'Closed' },
  // Confirm dialogs: a problem only moves forward, so every step past `assess` asks.
  confirmProblemTitle: { de: '{number} auf „{status}" setzen?', en: 'Move {number} to {status}?' },
  confirmProblem_known_error: {
    de: 'Das Problem wird als bekannter Fehler geführt und an die Standorte gespiegelt. Ein Status geht nicht zurück.',
    en: 'It is recorded as a known error and mirrored to the sites. A status cannot be moved backwards.',
  },
  confirmProblem_resolved: {
    de: 'Das Problem gilt als behoben. Von hier aus geht es nur noch auf „Geschlossen".',
    en: 'It is marked as fixed. From here it can only move on to Closed.',
  },
  confirmProblem_closed: {
    de: 'Ein geschlossenes Problem ist endgültig und kann nicht wieder geöffnet werden.',
    en: 'A closed problem is final and cannot be reopened.',
  },
  readOnlyHintProblems: {
    de: 'Nur-Lese-Zugriff — zum Anlegen oder Bearbeiten von Problemen wird die Berechtigung „Wartung: Genehmigen“ benötigt.',
    en: 'Read-only access — raising or managing problems needs the Maintenance: Approve permission.',
  },
  readOnlyHintKb: {
    de: 'Nur-Lese-Zugriff — zum Anlegen oder Bearbeiten von Artikeln wird die Berechtigung „Wartung: Schreiben“ benötigt.',
    en: 'Read-only access — writing articles needs the Maintenance: Write permission.',
  },
  toastProblemCreated: { de: '{number} angelegt', en: '{number} created' },
  toastProblemUpdated: { de: '{number} aktualisiert', en: '{number} updated' },
  toastProblemTransitioned: { de: '{number}: {status}', en: '{number} moved to {status}' },

  // ── kb lifecycle ──
  newKb: { de: 'Neuer Artikel', en: 'New article' },
  createKb: { de: 'Artikel anlegen', en: 'Create article' },
  emptyKb: { de: 'Keine Artikel', en: 'No articles' },
  emptyKbMsg: { de: 'Noch keine KB-Artikel.', en: 'No KB articles yet.' },
  colState: { de: 'Workflow', en: 'State' },
  colValidTo: { de: 'Gültig bis', en: 'Valid to' },
  kbDetailTitle: { de: 'Artikeldetails', en: 'Article details' },
  fieldBody: { de: 'Text', en: 'Body' },
  fieldBodyPh: {
    de: 'Vollständiger Artikeltext — Schritte, Diagnose, Behebung',
    en: 'Full article text — steps, diagnostics, resolution',
  },
  fieldSafety: { de: 'Sicherheitshinweise', en: 'Safety notes' },
  fieldSymptoms: { de: 'Symptome', en: 'Symptoms' },
  fieldSymptomsPh: { de: 'z. B. Pumpe taktet schnell ein/aus', en: 'e.g. Pump cycles on/off rapidly' },
  fieldCause: { de: 'Ursache', en: 'Cause' },
  fieldCausePh: { de: 'z. B. Verschlissene Wellendichtung', en: 'e.g. Worn shaft seal' },
  fieldFix: { de: 'Behebung', en: 'Fix' },
  fieldFixPh: { de: 'z. B. Dichtungssatz P/N 4471-A ersetzen', en: 'e.g. Replace seal kit P/N 4471-A' },
  fieldValidTo: { de: 'Gültig bis (JJJJ-MM-TT)', en: 'Valid to (YYYY-MM-DD)' },
  fieldValidToPh: { de: 'z. B. 2026-12-31', en: 'e.g. 2026-12-31' },
  fieldProblemId: { de: 'Verknüpftes Problem', en: 'Linked problem' },
  problemNone: { de: '— keines —', en: '— none —' },
  state_draft: { de: 'Entwurf', en: 'Draft' },
  state_review: { de: 'In Prüfung', en: 'In review' },
  state_published: { de: 'Veröffentlicht', en: 'Published' },
  state_retired: { de: 'Zurückgezogen', en: 'Retired' },
  transitionKbTo: { de: 'Weiter zu {state}', en: 'Move to {state}' },
  adminOnly: {
    de: 'Lifecycle-Übergänge erfordern Wartungs-Admin.',
    en: 'Lifecycle transitions require maintenance Admin.',
  },
  confirmKbTitle: { de: '{number} auf „{state}" setzen?', en: 'Move {number} to {state}?' },
  confirmKb_published: {
    de: 'Der Artikel geht live und wird an die Standorte gespiegelt.',
    en: 'The article goes live and is mirrored to the sites.',
  },
  confirmKb_retired: {
    de: 'Ein zurückgezogener Artikel wird nicht mehr angeboten und kann nicht wiederhergestellt werden.',
    en: 'A retired article is no longer offered and cannot be restored.',
  },
  toastKbCreated: { de: '{number} angelegt', en: '{number} created' },
  toastKbUpdated: { de: '{number} aktualisiert', en: '{number} updated' },
  toastKbTransitioned: { de: '{number}: {state}', en: '{number} moved to {state}' },

  // ── metrics ──
  metricsTitle: { de: 'Nutzung', en: 'Usage' },
  metricAttach: { de: 'Anhänge', en: 'Attaches' },
  metricHelpful: { de: 'Hilfreich', en: 'Helpful' },
  metricNotHelpful: { de: 'Nicht hilfreich', en: 'Not helpful' },
  metricRatio: { de: 'Hilfreich-Quote', en: 'Helpful ratio' },
  metricReadAcks: { de: 'Lesebestätigungen', en: 'Read acks' },
  // S-6: incidents linked to a problem / known-error.
  linkedIncidents: { de: 'Verknüpfte Incidents ({count})', en: 'Linked incidents ({count})' },
  noLinkedIncidents: { de: 'Keine verknüpften Incidents.', en: 'No linked incidents.' },
} satisfies Namespace;
